/**
 * RelatedTools — Cross-linking strip displayed at the bottom of tool pages.
 * Drop on any tool page with category prop and current tool slug to skip.
 *
 * <RelatedTools category="seo" current="keyword-research" />
 */
import { Link } from "react-router-dom";
import {
  Search, Target, Link as LinkIcon, BarChart3, FileText, Hash,
  Image, Type, Palette, Youtube, Wand2, Share2, Calculator,
  Globe, Sparkles, ArrowRight, Key, MessageSquare,
} from "lucide-react";

const CATALOG = {
  seo: [
    { slug: "keyword-research", title: "Keyword Research", desc: "Live SerpAPI keyword data + difficulty", Icon: Search, to: "/tools/seo?tool=keyword-research" },
    { slug: "ai-keyword-generator", title: "AI Keyword Generator", desc: "30 ideas with live search volume", Icon: Sparkles, to: "/tools/seo/ai" },
    { slug: "backlink-analyzer", title: "Backlink Analyzer", desc: "Domain authority + referring links", Icon: LinkIcon, to: "/tools/seo?tool=backlink-analyzer" },
    { slug: "rank-tracking", title: "Rank Tracking", desc: "Monitor your Google rankings", Icon: BarChart3, to: "/tools/seo?tool=rank-tracking" },
    { slug: "site-audit", title: "Site Audit", desc: "300+ technical SEO checks", Icon: Target, to: "/tools/seo?tool=site-audit" },
    { slug: "content-grader", title: "SEO Content Grader", desc: "Score & optimize any article", Icon: FileText, to: "/tools/seo/ai" },
  ],
  youtube: [
    { slug: "title-generator", title: "Title Generator", desc: "Hook-driven viral titles", Icon: Youtube, to: "/tools/youtube?tool=title-generator" },
    { slug: "tag-generator", title: "Tag Generator", desc: "Auto-suggest tags from topic", Icon: Hash, to: "/tools/youtube?tool=tag-generator" },
    { slug: "thumbnail-analyzer", title: "Thumbnail Analyzer", desc: "CTR predictions + variations", Icon: Image, to: "/tools/youtube?tool=thumbnail-analyzer" },
    { slug: "trend-finder", title: "Trend Finder", desc: "What's trending in your niche", Icon: BarChart3, to: "/tools/youtube?tool=trend-finder" },
  ],
  content: [
    { slug: "ai-article-writer", title: "AI Article Writer", desc: "Long-form SEO articles", Icon: FileText, to: "/tools/content?tool=ai-writer" },
    { slug: "blog-ideas", title: "Blog Idea Generator", desc: "Endless topic ideas by niche", Icon: Wand2, to: "/tools/content?tool=blog-ideas" },
    { slug: "meta-tags", title: "Meta Tag Generator", desc: "Title + description optimized", Icon: Type, to: "/tools/seo/ai" },
  ],
  social: [
    { slug: "hashtag-generator", title: "Hashtag Generator", desc: "Niche-perfect hashtag packs", Icon: Hash, to: "/tools/social-media?tool=hashtags" },
    { slug: "caption-writer", title: "Caption Writer", desc: "Hook + body + CTA", Icon: Share2, to: "/tools/social-media?tool=captions" },
  ],
  design: [
    { slug: "color-palette-generator", title: "Color Palette", desc: "Brand colors + complementary", Icon: Palette, to: "/free/color-palette-generator" },
    { slug: "logo-ideas", title: "Logo Ideas", desc: "Brand mark concepts", Icon: Sparkles, to: "/tools/design?tool=logo-ideas" },
  ],
  free: [
    { slug: "word-counter", title: "Word Counter", desc: "Words, chars, reading time", Icon: Type, to: "/free/word-counter" },
    { slug: "url-shortener", title: "URL Shortener", desc: "Short links + analytics", Icon: LinkIcon, to: "/free/url-shortener" },
    { slug: "password-generator", title: "Password Generator", desc: "Strong, random passwords", Icon: Key, to: "/free/password-generator" },
    { slug: "roi-calculator", title: "ROI Calculator", desc: "Marketing ROI instantly", Icon: Calculator, to: "/free/roi-calculator" },
    { slug: "color-palette-generator", title: "Color Palette", desc: "Brand colors picker", Icon: Palette, to: "/free/color-palette-generator" },
    { slug: "hashtag-generator", title: "Hashtag Pack", desc: "Trending hashtags", Icon: Hash, to: "/free/hashtag-generator" },
  ],
};

const CATEGORY_LABEL = {
  seo: "SEO Tools",
  youtube: "YouTube SEO",
  content: "Content Marketing",
  social: "Social Media",
  design: "Design Tools",
  free: "Free Utilities",
};

const BROWSE_LINK = {
  seo: "/tools/seo",
  youtube: "/tools/youtube",
  content: "/tools/content",
  social: "/tools/social-media",
  design: "/tools/design",
  free: "/free-tools",
};

export default function RelatedTools({
  category = "seo",
  current = "",
  limit = 6,
  title,
  showFree = true,
}) {
  const main = (CATALOG[category] || CATALOG.seo).filter((t) => t.slug !== current);
  const items = main.slice(0, limit);
  // Sprinkle 2 free utility tools so users discover them across the site
  const freeItems = showFree && category !== "free"
    ? CATALOG.free.filter((t) => t.slug !== current).slice(0, 2)
    : [];

  return (
    <section data-testid="related-tools" style={{
      padding: "40px 28px",
      background: "linear-gradient(135deg, #F8FAFC, #FAF5FF)",
      borderRadius: 16,
      border: "1px solid #E2E8F0",
      marginTop: 40,
    }}>
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18, flexWrap: "wrap", gap: 12 }}>
        <div>
          <strong style={{ fontSize: 11, color: "#7C3AED", fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase" }}>
            More {CATEGORY_LABEL[category] || "Tools"}
          </strong>
          <h3 style={{ margin: "4px 0 0", fontSize: 22, fontWeight: 800, color: "#0F172A", letterSpacing: "-0.01em" }}>
            {title || `Related tools to keep your ${CATEGORY_LABEL[category] || "SEO"} workflow flowing`}
          </h3>
        </div>
        <Link
          to={BROWSE_LINK[category] || "/tools"}
          data-testid="related-tools-browse-all"
          style={{ padding: "8px 14px", background: "#FFFFFF", color: "#7C3AED", border: "1px solid #E9D5FF", borderRadius: 10, textDecoration: "none", fontWeight: 600, fontSize: 13, display: "inline-flex", alignItems: "center", gap: 6 }}
        >
          Browse all <ArrowRight size={12}/>
        </Link>
      </header>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 12 }}>
        {items.map((t) => (
          <Link
            key={t.slug}
            to={t.to}
            data-testid={`related-tool-${t.slug}`}
            style={{
              display: "flex", alignItems: "center", gap: 12,
              padding: "12px 14px",
              background: "#FFFFFF",
              border: "1px solid #E2E8F0",
              borderRadius: 12,
              textDecoration: "none",
              color: "#0F172A",
              transition: "all 0.15s ease",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#A78BFA"; e.currentTarget.style.transform = "translateY(-2px)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#E2E8F0"; e.currentTarget.style.transform = "translateY(0)"; }}
          >
            <div style={{ width: 36, height: 36, borderRadius: 9, background: "linear-gradient(135deg, #7C3AED, #EC4899)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", flexShrink: 0 }}>
              <t.Icon size={16}/>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ margin: 0, fontWeight: 700, fontSize: 13, color: "#0F172A" }}>{t.title}</p>
              <p style={{ margin: "2px 0 0", fontSize: 11, color: "#64748B", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.desc}</p>
            </div>
          </Link>
        ))}
      </div>

      {freeItems.length > 0 && (
        <>
          <div style={{ marginTop: 24, padding: "12px 16px", background: "linear-gradient(135deg, #FEF3C7, #FED7AA)", borderRadius: 10, display: "flex", alignItems: "center", gap: 10 }}>
            <Sparkles size={14} color="#92400E"/>
            <strong style={{ color: "#92400E", fontSize: 13 }}>Also try these 100% free tools — no signup needed</strong>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 12, marginTop: 10 }}>
            {freeItems.map((t) => (
              <Link
                key={t.slug}
                to={t.to}
                data-testid={`related-free-${t.slug}`}
                style={{
                  display: "flex", alignItems: "center", gap: 12,
                  padding: "10px 12px",
                  background: "#FFFFFF",
                  border: "1px dashed #F59E0B",
                  borderRadius: 12,
                  textDecoration: "none",
                  color: "#0F172A",
                  fontSize: 13,
                }}
              >
                <t.Icon size={16} color="#F59E0B"/>
                <strong>{t.title}</strong>
                <span style={{ marginLeft: "auto", fontSize: 10, padding: "2px 6px", background: "#FEF3C7", color: "#92400E", borderRadius: 4, fontWeight: 700 }}>FREE</span>
              </Link>
            ))}
          </div>
        </>
      )}
    </section>
  );
}
